import {getHTMLFromFragment} from "@tiptap/core";
import Paragraph from "@tiptap/extension-paragraph";
import type {MarkdownNodeSpec, MarkdownStorage} from "tiptap-markdown";

/*
    How the field stores its text. Kept apart from the extension list, which loads StarterKit, so
    a test can build an editor from these without it.
 */

declare module "@tiptap/core" {
    interface Storage {
        markdown: MarkdownStorage;
    }
}

/*
    HTML stays on: markdown has no underline and no alignment, so those are written as `<u>` and
    `<p style="text-align: …">` inside the markdown. Pasted text is never read as markdown.
 */
export const MARKDOWN_OPTIONS = {
    html: true,
    tightLists: true,
    bulletListMarker: '-',
    linkify: false,
    breaks: false,
    transformPastedText: false,
    transformCopiedText: false,
} as const;

/** Markdown has no empty paragraph — any run of empty lines reads back as one break — so one is written as this. */
const BLANK_LINE = '&nbsp;';
const NO_BREAK_SPACE = String.fromCharCode(0xa0);

type ProseMirrorNode = Parameters<MarkdownNodeSpec['serialize']>[1];

/** An empty paragraph between text is a blank line the author left; at either end it is nothing. */
const isBlankLine = (parent: ProseMirrorNode, index: number) => {
    let before = false;
    let after = false;

    parent.forEach((child, _offset, at) => {
        if (child.type.name === 'paragraph' && child.childCount === 0) return;
        if (at < index) before = true;
        if (at > index) after = true;
    });

    return before && after;
};

const paragraphMarkdown: MarkdownNodeSpec = {
    serialize(state, node, parent, index) {
        // Not even closed when it is no blank line: a closed block puts a line break before what follows.
        if (node.childCount === 0 && parent.type.name === 'doc') {
            if (!isBlankLine(parent, index)) return;

            state.write(BLANK_LINE);
            state.closeBlock(node);
            return;
        }

        // Markdown has no alignment, so an aligned paragraph is written as HTML.
        if (!node.attrs.textAlign) {
            state.renderInline(node);
            state.closeBlock(node);
            return;
        }

        const html = getHTMLFromFragment(node.content, this.editor.schema);
        state.write(`<p style="text-align: ${node.attrs.textAlign}">${html}</p>`);
        state.closeBlock(node);
    },
    parse: {
        // A written blank line arrives as `<p>&nbsp;</p>`; it loads as the empty paragraph it was.
        updateDOM(element) {
            element.querySelectorAll('p').forEach(paragraph => {
                if (paragraph.childNodes.length === 1 && paragraph.textContent === NO_BREAK_SPACE) {
                    paragraph.replaceChildren();
                }
            });
        },
    },
};

/** The paragraph, written back with its blank lines and its alignment. */
export const MarkdownParagraph = Paragraph.extend({
    addStorage() {
        return {markdown: paragraphMarkdown};
    },
});
